import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ForumSeo } from '@/components/ForumSeo'
import { RichEditor } from '@/components/richtext/RichEditor'
import { IconLoader2 } from '@tabler/icons-react'
import { Button } from '@/components/ui/button'
import { apiFetch } from '@/lib/api'
import { useAuthLoading, useConfirmedGuest, useSessionUser } from '@/lib/auth'
import {
  forumAlertError,
  forumCard,
  forumCardHeader,
  forumField,
  forumKicker,
  forumLoaderRow,
  forumMutedSm,
  forumSectionBar,
  forumTitleMd,
} from '@/lib/forumUi'
import type { ForumCategorySummary } from '@/lib/types'
import { forumPageTitle, truncateForMetaDescription } from '~shared/seo'

export function NewThreadPage() {
  const me = useSessionUser()
  const authLoading = useAuthLoading()
  const guest = useConfirmedGuest()
  const navigate = useNavigate()

  const [categories, setCategories] = useState<ForumCategorySummary[]>([])
  const [categorySlug, setCategorySlug] = useState('')
  const [title, setTitle] = useState('')
  const [content, setContent] = useState<unknown>({ type: 'doc', content: [{ type: 'paragraph' }] })
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void apiFetch<{ categories: ForumCategorySummary[] }>(`/api/categories`).then(payload => {
      setCategories(payload.categories ?? [])
      const first = payload.categories?.[0]?.slug ?? ''
      setCategorySlug(cs => (cs ? cs : first))
    })
  }, [])

  const slugOptions = useMemo(() => categories.map(c => ({ label: c.name, value: c.slug })), [categories])

  if (authLoading) {
    return (
      <div className={forumLoaderRow} aria-busy="true" aria-live="polite">
        <IconLoader2 className="size-5 shrink-0 animate-spin" aria-hidden="true" />
        Checking your session…
      </div>
    )
  }

  if (guest) {
    return (
      <section className={`${forumCard} px-5 py-10 md:px-6`} aria-labelledby="new-signed-out-heading">
        <ForumSeo
          title={forumPageTitle('New topic')}
          description={truncateForMetaDescription(
            'Publish a respectful North Kingstown RI topic: choose a board, clear title, and first post.',
          )}
          noindex
        />
        <h1 id="new-signed-out-heading" className={forumTitleMd}>
          New topic
        </h1>
        <p className={`${forumMutedSm} mt-2 text-zinc-600`}>You must be signed in to start a topic.</p>
        <Link className="mt-5 inline-block" to="/sign-in?next=/new">
          <Button>Sign in</Button>
        </Link>
      </section>
    )
  }

  if (!me) return null

  return (
    <div className="space-y-6">
      <ForumSeo
        title={forumPageTitle('New topic')}
        description={truncateForMetaDescription(
          'Publish a respectful North Kingstown RI topic: choose a board, clear title, and first post.',
        )}
        noindex
      />
      <header className={forumCard}>
        <div className={forumCardHeader}>
          <p className={forumKicker}>Post</p>
          <h1 className={forumTitleMd}>New topic</h1>
          <p className={`${forumMutedSm} mt-2 max-w-[70ch] text-zinc-600`}>
            Pick a board, enter a clear title, then write the first post.
          </p>
        </div>
      </header>

      {error ? <div role="alert" className={forumAlertError}>{error}</div> : null}

      <div className={forumCard}>
        <div className={forumSectionBar}>
          <span className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Topic details</span>
        </div>

        <div className="grid gap-4 px-5 py-5 md:grid-cols-2 md:gap-6 md:px-6">
          <div className="md:col-span-1">
            <label className="mb-2 block text-[13px] font-semibold text-zinc-800" htmlFor="new-thread-category">
              Category
            </label>
            <select id="new-thread-category" className={forumField} value={categorySlug} onChange={evt => setCategorySlug(evt.target.value)}>
              {slugOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="mb-2 block text-[13px] font-semibold text-zinc-800" htmlFor="new-thread-title">
              Title
            </label>
            <input
              id="new-thread-title"
              value={title}
              onChange={evt => setTitle(evt.target.value)}
              className={forumField}
              placeholder="Short, specific subject line"
              required
            />
          </div>
        </div>
      </div>

      <div className={forumCard}>
        <div className={forumSectionBar}>
          <span className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Body</span>
        </div>
        <div className="border-t border-zinc-100 px-5 py-4 md:px-6">
          <RichEditor initialJSON={content} onChangeJSON={setContent} />
        </div>
      </div>

      <div className="flex flex-wrap gap-3 overflow-hidden rounded-2xl border border-zinc-200 bg-white px-5 py-4 shadow-sm shadow-zinc-950/5 md:px-6">
        <Button
          type="button"
          className="rounded-lg bg-sky-600 font-semibold text-white hover:bg-sky-500"
          onClick={async () => {
            try {
              setError(null)
              const payload = await apiFetch<{ thread?: { slug: string } }>(`/api/threads`, {
                method: 'POST',
                json: {
                  categorySlug,
                  title,
                  content,
                },
              })

              navigate(`/t/${payload.thread?.slug}`)
            } catch {
              setError('Could not publish. Check HTTPS links in the body and try again.')
            }
          }}
        >
          Publish topic
        </Button>

        <Button type="button" variant="outline" className="rounded-lg font-semibold border-zinc-200" onClick={() => void apiFetch(`/api/drafts`, {
          method: 'POST',
          json: {
            threadId: null,
            content: JSON.stringify(content),
          },
        })}>
          Save draft (KV)
        </Button>
      </div>
    </div>
  )
}
